import React, { useState } from 'react';
import { Button, Drawer, message, Tooltip, Typography } from 'antd';
import { observer } from 'mobx-react';

import { useHistory } from 'react-router-dom';
import api from '../../../utils/api';
import QuoteStore from '../../../stores/QuoteStore';

import { QuoteLineItemProps, QuoteProps } from '../../../types/types';
import {
    ArrowLeftOutlined,
    EditOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    DownloadOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';

import dayjs from 'dayjs';
import QuoteRevisions from '../QuoteRevisions';
import ComingSoonFeaturesStore from '../../../stores/ComingSoonFeaturesStore';
import FileDownload from 'js-file-download';

const { Title } = Typography;

export interface IProps {
    quote?: QuoteProps | null;
    quote_line_items: QuoteLineItemProps[];
    editQuote: boolean;
    loadParentLineItems?: () => void;
    loading?: boolean;
    goCheckoutPage?: () => void;
    parentLineItems?: any[];
    quoteExpired?: boolean;
}
import { Tag } from 'antd';
import { Quote } from 'quotes-frontend/src/stores/types';

const RenderQuoteStatus = (status: string | undefined) => {
    switch (status) {
        case 'In Progress':
            return <Tag color="blue">Quoting</Tag>;
        case 'Completed':
            return <Tag color="green">Completed</Tag>;
        case 'Cancelled':
            return <Tag color="red">Cancelled</Tag>;
        case 'Draft':
            return <Tag color={'grey'}> Draft</Tag>;
        default:
            return <Tag color="default"></Tag>;
    }
};
const QuoteDetailHeader: React.FC<IProps> = props => {
    const history = useHistory();

    const [showModificationsDrawer, setShowModificationsDrawer] = useState<boolean>(false);
    const [loadingDownloadPDF, setLoadingDownloadPDF] = useState<boolean>(false);

    const {
        quote,
        quote_line_items,
        loading,
        editQuote,
        loadParentLineItems,
        goCheckoutPage,
        parentLineItems,
        quoteExpired,
    } = props;

    const handleClickNewRevision = () => {
        if (quote?.id) {
            QuoteStore.createQuoteNewRevision(quote.id)
                .then(data => {
                    message.success(`Revision ${data.name} created`);
                    history.push(`/my-account/quotes/${data.id}/order/`);
                })
                .catch(() => {
                    message.error('Something went wrong!');
                });
        }
    };

    const handleClickPrintPDF = (quote: QuoteProps) => {
        setLoadingDownloadPDF(true);
        api.Quotes.downloadQuote(quote.id)
            .then(data => {
                FileDownload(data, `${quote.name}.pdf`);
                const file = new Blob([data], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL, '_blank');
            })
            .catch(error => {
                console.error('Error downloading PDF:', error);
                message.error(error.message || 'Failed to download PDF. Please try again.');
            })
            .finally(() => {
                setLoadingDownloadPDF(false);
            });
    };

    const canRequestARequote = quote?.completed || quote?.customer_status == 'Cancelled';

    return (
        <div className="quote-detail-header">
            <div className="bg-white border-bottom mb-3 pt-3 pb-3" style={{ paddingBottom: '2rem' }}>
                <div className="container">
                    <div className={'flex flex-row justify-between'}>
                        <div className={'flex flex-row items-center justify-center'}>
                            <Button
                                type="link"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => {
                                    history.goBack();
                                }}
                                className="text-primary m-0 p-0"
                            />

                            <div className="flex flex-column flex-md-row justify-content-between mb-3">
                                <div className="d-flex flex-column flex-md-row">
                                    <Title level={1} className="mb-2 mr-2">
                                        {quote?.name}
                                    </Title>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {quote?.customer_status ? RenderQuoteStatus(quote?.customer_status) : null}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            {editQuote ? (
                                <Button onClick={() => history.goBack()}>Cancel</Button>
                            ) : quote?.completed && !quoteExpired ? (
                                <Button
                                    key={1}
                                    onClick={handleClickNewRevision}
                                    type="primary"
                                    icon={<EditOutlined />}
                                    disabled={quoteExpired}
                                >
                                    Modify Quote
                                </Button>
                            ) : null}
                        </div>
                    </div>

                    <div className="d-flex flex-md-row flex-column align-items-md-end justify-content-between">
                        <div className="d-flex flex-row" style={{ columnGap: '6px' }}>
                            {quote?.completed ? (
                                <Button
                                    className="ant-btn-xs"
                                    icon={<DownloadOutlined />}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => {
                                        if (quote?.id) {
                                            handleClickPrintPDF(quote);
                                        }
                                    }}
                                    loading={loadingDownloadPDF}
                                >
                                    Download
                                </Button>
                            ) : null}

                            {quote?.completed ? (
                                <>
                                    <Button
                                        className="ant-btn-xs"
                                        icon={<UnorderedListOutlined />}
                                        onClick={() => setShowModificationsDrawer(true)}
                                        disabled={quoteExpired}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        View Modifications
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {parentLineItems?.length ? (
                                        <Button
                                            type="default"
                                            className="ant-btn-xs"
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            icon={<PlusOutlined />}
                                            loading={loading}
                                            onClick={() => {
                                                if (quote?.duplicated_from) {
                                                    loadParentLineItems && loadParentLineItems();
                                                }
                                            }}
                                        >
                                            Add Original Line Items
                                        </Button>
                                    ) : null}
                                </>
                            )}

                            {canRequestARequote ? (
                                <Tooltip
                                    placement="bottomLeft"
                                    title={
                                        'Create a copy of the original quote, to add more products, or modify the properties of existing products.'
                                    }
                                >
                                    <Button
                                        className="ant-btn-xs"
                                        icon={<PlusOutlined />}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        onClick={() => {
                                            QuoteStore.requote(quote?.id)
                                                .then(response => {
                                                    history.replace(`/my-account/quotes/${response.id}/continue/`);
                                                })
                                                .catch(error => {
                                                    message.error('Something went wrong!');
                                                });
                                        }}
                                        disabled={!quote_line_items.length}
                                    >
                                        Submit for a Requote
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </div>

                        <div className="mt-2">
                            {quote?.completed && quote?.completed_at ? (
                                <span>
                                    <InfoCircleOutlined className="mr-1" />
                                    {`Submitted on ${dayjs(quote?.completed_at).format('YYYY-MM-DD')} ${
                                        quote?.contact?.fullname ? `By ${quote?.contact?.fullname}` : ''
                                    }`}
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            {quote?.id ? (
                <>
                    <Drawer
                        title="Quote Modifications"
                        placement="right"
                        closable={true}
                        width={'auto'}
                        bodyStyle={{ padding: 0, width: '95vw', maxWidth: '720px' }}
                        onClose={() => {
                            setShowModificationsDrawer(false);
                        }}
                        open={showModificationsDrawer}
                    >
                        <QuoteRevisions parent_quote={quote.id} />
                    </Drawer>
                </>
            ) : null}
        </div>
    );
};

export default observer(QuoteDetailHeader);
